import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { adminPayouts, type AdminPayout } from "@/mocks/admin";
import PayoutDrawer from "./components/PayoutDrawer";

type StatusFilter = "all" | AdminPayout["status"];

const statusFilters: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "processing", label: "Processing" },
  { id: "paid", label: "Paid" },
  { id: "failed", label: "Failed" },
];

const statusPill: Record<AdminPayout["status"], string> = {
  paid: "bg-primary-100 text-primary-700",
  pending: "bg-background-200 text-foreground-700",
  processing: "bg-accent-100 text-accent-700",
  failed: "bg-accent-200 text-accent-800",
};

const statusLabel: Record<AdminPayout["status"], string> = {
  paid: "Paid",
  pending: "Pending",
  processing: "Processing",
  failed: "Failed",
};

const methodIcon: Record<AdminPayout["method"], string> = {
  ACH: "ri-bank-line",
  Instant: "ri-flashlight-line",
  Check: "ri-mail-line",
};

export default function AdminPayoutsPage() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [payouts, setPayouts] = useState<AdminPayout[]>(adminPayouts);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payouts.filter((p) => {
      if (filter !== "all" && p.status !== filter) return false;
      if (!q) return true;
      return (
        p.cleanerName.toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q) ||
        p.periodLabel.toLowerCase().includes(q)
      );
    });
  }, [search, filter, payouts]);

  const counts = useMemo(
    () => ({
      all: payouts.length,
      pending: payouts.filter((p) => p.status === "pending").length,
      processing: payouts.filter((p) => p.status === "processing").length,
      paid: payouts.filter((p) => p.status === "paid").length,
      failed: payouts.filter((p) => p.status === "failed").length,
    }),
    [payouts],
  );

  const totals = useMemo(() => {
    const pending = payouts
      .filter((p) => p.status === "pending")
      .reduce((sum, p) => sum + p.net, 0);
    const processing = payouts
      .filter((p) => p.status === "processing")
      .reduce((sum, p) => sum + p.net, 0);
    const paid = payouts
      .filter((p) => p.status === "paid")
      .reduce((sum, p) => sum + p.net, 0);
    const failed = payouts
      .filter((p) => p.status === "failed")
      .reduce((sum, p) => sum + p.net, 0);
    return { pending, processing, paid, failed };
  }, [payouts]);

  const active = payouts.find((p) => p.id === activeId) || null;

  const updateStatus = (id: string, next: AdminPayout["status"]) => {
    setPayouts((arr) =>
      arr.map((p) =>
        p.id === id
          ? {
              ...p,
              status: next,
              paidAt: next === "paid" ? "Just now" : p.paidAt,
            }
          : p,
      ),
    );
  };

  const handlePayAll = () => {
    const pendingIds = payouts.filter((p) => p.status === "pending").map((p) => p.id);
    if (pendingIds.length === 0) {
      setToast("Nothing to pay right now");
      return;
    }
    setPayouts((arr) =>
      arr.map((p) =>
        pendingIds.includes(p.id) ? { ...p, status: "processing" as const } : p,
      ),
    );
    setToast(`Sent ${pendingIds.length} payout${pendingIds.length === 1 ? "" : "s"} to processing`);
  };

  return (
    <AdminLayout
      title="Payouts"
      subtitle="Pay your cleaners and track every transfer"
      action={
        <button
          onClick={handlePayAll}
          disabled={counts.pending === 0}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:bg-background-200 disabled:text-foreground-500 text-background-50 text-sm font-medium cursor-pointer disabled:cursor-not-allowed whitespace-nowrap"
        >
          <i className="ri-send-plane-line"></i>
          Pay all pending
        </button>
      }
    >
      <div className="space-y-4">
        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-background-50 border border-background-200 rounded-xl px-4 py-3.5">
            <p className="text-[10px] uppercase tracking-widest text-foreground-500">
              Pending
            </p>
            <p className="font-heading text-xl text-foreground-950 mt-1">
              ${totals.pending.toLocaleString()}
            </p>
            <p className="text-[10px] text-foreground-600 mt-0.5">
              {counts.pending} payout{counts.pending === 1 ? "" : "s"}
            </p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-xl px-4 py-3.5">
            <p className="text-[10px] uppercase tracking-widest text-foreground-500">
              Processing
            </p>
            <p className="font-heading text-xl text-foreground-950 mt-1">
              ${totals.processing.toLocaleString()}
            </p>
            <p className="text-[10px] text-foreground-600 mt-0.5">
              {counts.processing} in flight
            </p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-xl px-4 py-3.5">
            <p className="text-[10px] uppercase tracking-widest text-foreground-500">
              Paid this month
            </p>
            <p className="font-heading text-xl text-foreground-950 mt-1">
              ${totals.paid.toLocaleString()}
            </p>
            <p className="text-[10px] text-foreground-600 mt-0.5">
              {counts.paid} cleared
            </p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-xl px-4 py-3.5">
            <p className="text-[10px] uppercase tracking-widest text-foreground-500">
              Needs attention
            </p>
            <p className="font-heading text-xl text-foreground-950 mt-1">
              ${totals.failed.toLocaleString()}
            </p>
            <p className="text-[10px] text-foreground-600 mt-0.5">
              {counts.failed} failed
            </p>
          </div>
        </div>

        {/* Filter row */}
        <div className="bg-background-50 border border-background-200 rounded-xl p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, ID or period"
              className="w-full bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-500 rounded-md pl-9 pr-3 py-2 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 overflow-x-auto">
            {statusFilters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  filter === f.id
                    ? "bg-background-50 text-foreground-950"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {f.label}
                <span className="ml-1.5 text-foreground-500">
                  {counts[f.id as keyof typeof counts]}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-3">
                <i className="ri-search-eye-line text-foreground-500 text-xl"></i>
              </span>
              <p className="text-sm font-medium text-foreground-800">
                No payouts match
              </p>
              <p className="text-xs text-foreground-600 mt-1">
                Try a different search or filter
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-background-200">
              {filtered.map((p) => (
                <li
                  key={p.id}
                  onClick={() => setActiveId(p.id)}
                  className="flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3.5 hover:bg-background-100/60 transition-colors cursor-pointer"
                >
                  <img
                    src={p.cleanerAvatar}
                    alt={p.cleanerName}
                    className="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover object-top flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-foreground-950 truncate">
                        {p.cleanerName}
                      </p>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusPill[p.status]}`}
                      >
                        {statusLabel[p.status]}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-600 truncate">
                      {p.id} · {p.periodLabel} · {p.jobs} jobs
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-foreground-700 whitespace-nowrap">
                    <i className={`${methodIcon[p.method]} text-foreground-600`}></i>
                    {p.method}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-heading text-base text-foreground-950">
                      ${p.net.toLocaleString()}
                    </p>
                    <p className="text-[10px] text-foreground-500">
                      {p.status === "paid"
                        ? `Paid ${p.paidAt?.split(",")[0] ?? ""}`
                        : `Scheduled ${p.scheduledFor}`}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <PayoutDrawer
        payout={active}
        onClose={() => setActiveId(null)}
        onPay={() => {
          if (!active) return;
          updateStatus(active.id, "processing");
          setActiveId(null);
          setToast(`${active.cleanerName} payout sent to processing`);
        }}
        onRetry={() => {
          if (!active) return;
          updateStatus(active.id, "processing");
          setActiveId(null);
          setToast(`Retrying payout for ${active.cleanerName}`);
        }}
      />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] bg-foreground-950 text-background-50 text-xs px-4 py-2.5 rounded-full shadow animate-fade-up flex items-center gap-2">
          <i className="ri-check-line"></i>
          {toast}
        </div>
      )}
    </AdminLayout>
  );
}